import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { deleteRole, getRoles } from "src/api/settings";
import useHasPermissions from "src/hooks/useHasPermissions";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import CreateRole from "src/components/modals/CreateRole";
import PermissionWrapper from "src/components/PermissionWrapper";
import { Button } from "src/components/ui/button";
import { DataTable, SettingsPanel, type Column } from "src/components/common";
import { showSuccessToast } from "src/lib/utils";
import type { Role } from "src/types/settings";

const RoleSettings = () => {
	const canEdit = useHasPermissions("update_role");
	const canDelete = useHasPermissions("delete_role");
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery({ queryKey: ["roles"], queryFn: () => getRoles() });

	const { mutate: deleteRoleMutate } = useMutation(deleteRole, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Role deleted");
			queryClient.invalidateQueries(["roles"]);
		},
	});

	const roles = data?.data ?? [];

	const columns: Column<Role>[] = [
		{ key: "name", header: "Role", primary: true, cell: (row) => <span className="tw-capitalize tw-font-medium">{row.name ?? "—"}</span> },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[110px] tw-text-right",
			cell: (row) => (
				<div className="tw-flex tw-justify-end tw-gap-1">
					{canEdit && (
						<CustomModal
							title="Edit Role"
							size="normal"
							trigger={
								<button
									type="button"
									aria-label="Edit role"
									className="tw-rounded-lg tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text"
								>
									<Pencil className="tw-h-3.5 tw-w-3.5" />
								</button>
							}
						>
							<CreateRole role={row} />
						</CustomModal>
					)}
					{canDelete && (
						<CustomConfirmDialog
							trigger={
								<button
									type="button"
									aria-label="Delete role"
									className="tw-rounded-lg tw-p-1.5 tw-text-red tw-transition-colors hover:tw-bg-red-tint"
								>
									<Trash2 className="tw-h-3.5 tw-w-3.5" />
								</button>
							}
							mutateFn={() => deleteRoleMutate({ uuid: row.uuid ?? "" })}
						/>
					)}
				</div>
			),
		},
	];

	return (
		<SettingsPanel
			title="Roles & Permissions"
			description="Control what each team member can access."
			actions={
				<PermissionWrapper permission="add_role">
					<CustomModal
						title="Create Role"
						size="normal"
						trigger={
							<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
								<Plus className="tw-h-4 tw-w-4" />
								Create Role
							</Button>
						}
					>
						<CreateRole />
					</CustomModal>
				</PermissionWrapper>
			}
		>
			<DataTable
				data={roles}
				columns={columns}
				rowKey={(row, index) => row.uuid ?? index}
				loading={isLoading}
				emptyTitle="No roles yet"
				emptyDescription="Create a role to group permissions and assign them to your team."
			/>
		</SettingsPanel>
	);
};

export default RoleSettings;
